import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  cacheSongAsBlob as cacheBlob,
  getCachedBlobUrl,
  hasSomeCached,
  hasAllCached,
  hasStaleEntries,
  revokeAll,
  initOfflineCache,
} from "../utils/offlineCache";

export function useLiveMode({ songs, tenantContext, resolveSongPdfUrl, showToast }) {
  const navigate = useNavigate();
  const [liveMode, setLiveMode] = useState(null);
  const [preparingOfflineSetlistId, setPreparingOfflineSetlistId] = useState(null);
  const [, setCacheVersion] = useState(0);
  const liveModeUrlCache = useRef({});

  // Hydrate the in-memory URL cache from IndexedDB on mount so offline-ready
  // state is accurate before the user interacts with the setlists tab.
  useEffect(() => {
    initOfflineCache().then(() => setCacheVersion((v) => v + 1));
  }, []);

  // Revoke all blob URLs on unmount to free memory. revokeAll() also resets
  // the init promise so the next mount re-hydrates cleanly from IDB.
  useEffect(() => () => revokeAll(), []);

  const cacheSongAsBlob = useCallback(async (setlistId, song) => {
    try {
      await cacheBlob(setlistId, song, tenantContext);
      const blobUrl = getCachedBlobUrl(setlistId, song.id);
      if (blobUrl) liveModeUrlCache.current[song.id] = blobUrl;
    } catch (err) {
      console.warn("Background blob cache failed for song:", song.id, err);
    }
  }, [tenantContext]);

  // Launches live mode from a setlist. Captures a frozen snapshot of songs for
  // the session — Firestore listeners continue but never mutate liveMode.songs.
  const handleGoLive = useCallback(async (setlist) => {
    const setlistSongs = (setlist.songIds ?? [])
      .map((id) => songs.find((s) => s.id === id))
      .filter(Boolean);
    if (setlistSongs.length === 0) {
      showToast("Add songs to this setlist before going live.");
      return;
    }
    setLiveMode({
      setlistId: setlist.id,
      setlistName: setlist.name,
      songs: setlistSongs,
      currentIndex: 0,
      resolvedUrl: "",
      isLoadingUrl: true,
    });

    const offlineUrl = getCachedBlobUrl(setlist.id, setlistSongs[0].id);
    if (offlineUrl) {
      liveModeUrlCache.current[setlistSongs[0].id] = offlineUrl;
      setLiveMode((prev) => prev && { ...prev, resolvedUrl: offlineUrl, isLoadingUrl: false });
    } else {
      try {
        const url = await resolveSongPdfUrl(setlistSongs[0]);
        liveModeUrlCache.current[setlistSongs[0].id] = url;
        setLiveMode((prev) => prev && { ...prev, resolvedUrl: url, isLoadingUrl: false });
        cacheSongAsBlob(setlist.id, setlistSongs[0]).catch(() => {});
      } catch (err) {
        console.error("Error resolving live mode URL:", err);
        setLiveMode((prev) => prev && { ...prev, isLoadingUrl: false });
      }
    }

    setlistSongs.slice(1).forEach((song) => {
      cacheSongAsBlob(setlist.id, song).catch(() => {});
    });
  }, [songs, resolveSongPdfUrl, cacheSongAsBlob, showToast]);

  const isSetlistOfflineReady = useCallback(
    (setlistId) => hasSomeCached(setlistId),
    [] // hasSomeCached reads module-level state; re-render from setCacheVersion is sufficient
  );

  // Returns true when the setlist is cached but at least one song's PDF has
  // been replaced since caching. Synchronous — no IDB round-trip.
  const isSetlistStale = useCallback(
    (setlist, setlistSongs) => {
      if (!hasSomeCached(setlist.id)) return false;
      return hasStaleEntries(setlist.id, setlistSongs);
    },
    []
  );

  const handlePrepareOffline = useCallback(async (setlist) => {
    const setlistSongs = (setlist.songIds ?? [])
      .map((id) => songs.find((s) => s.id === id))
      .filter(Boolean);
    if (setlistSongs.length === 0) return;
    setPreparingOfflineSetlistId(setlist.id);
    await Promise.all(setlistSongs.map((song) => cacheSongAsBlob(setlist.id, song)));
    setPreparingOfflineSetlistId(null);
    setCacheVersion((v) => v + 1);
  }, [songs, cacheSongAsBlob]);

  const handleLiveModeNavigate = useCallback(async (newIndex) => {
    if (!liveMode) return;
    const song = liveMode.songs[newIndex];
    setLiveMode((prev) => prev && { ...prev, currentIndex: newIndex, resolvedUrl: "", isLoadingUrl: true });

    const offlineUrl = getCachedBlobUrl(liveMode.setlistId, song.id);
    if (offlineUrl) {
      liveModeUrlCache.current[song.id] = offlineUrl;
      setLiveMode((prev) => prev && { ...prev, resolvedUrl: offlineUrl, isLoadingUrl: false });
      return;
    }
    const cached = liveModeUrlCache.current[song.id];
    if (cached) {
      setLiveMode((prev) => prev && { ...prev, resolvedUrl: cached, isLoadingUrl: false });
      return;
    }
    try {
      const url = await resolveSongPdfUrl(song);
      liveModeUrlCache.current[song.id] = url;
      setLiveMode((prev) => prev && { ...prev, resolvedUrl: url, isLoadingUrl: false });
      cacheSongAsBlob(liveMode.setlistId, song).catch(() => {});
    } catch (err) {
      console.error("Error resolving live mode URL:", err);
      setLiveMode((prev) => prev && { ...prev, isLoadingUrl: false });
    }
  }, [liveMode, resolveSongPdfUrl, cacheSongAsBlob]);

  const handleLiveModeClose = useCallback(() => setLiveMode(null), []);

  const handleCoachmarkDismiss = useCallback(
    () => navigate("/", { replace: true }),
    [navigate]
  );

  // Launched from OnboardingFlow via AppActionsContext — same freeze contract as handleGoLive.
  const startLiveModeForSetlist = useCallback(async (setlistId, setlistName, songsArr) => {
    if (!songsArr || songsArr.length === 0) return;
    setLiveMode({
      setlistId,
      setlistName,
      songs: songsArr,
      currentIndex: 0,
      resolvedUrl: "",
      isLoadingUrl: true,
    });
    const song0 = songsArr[0];
    const offlineUrl = getCachedBlobUrl(setlistId, song0.id);
    if (offlineUrl) {
      liveModeUrlCache.current[song0.id] = offlineUrl;
      setLiveMode((prev) => prev && { ...prev, resolvedUrl: offlineUrl, isLoadingUrl: false });
    } else {
      try {
        const url = await resolveSongPdfUrl(song0);
        liveModeUrlCache.current[song0.id] = url;
        setLiveMode((prev) => prev && { ...prev, resolvedUrl: url, isLoadingUrl: false });
        cacheSongAsBlob(setlistId, song0).catch(() => {});
      } catch (err) {
        console.error("Error resolving live mode URL:", err);
        setLiveMode((prev) => prev && { ...prev, isLoadingUrl: false });
      }
    }
    songsArr.slice(1).forEach((song) => {
      cacheSongAsBlob(setlistId, song).catch(() => {});
    });
  }, [resolveSongPdfUrl, cacheSongAsBlob]);

  const isLiveModeOfflineReady = liveMode
    ? hasAllCached(liveMode.setlistId, liveMode.songs.map((s) => s.id))
    : false;

  return {
    liveMode,
    preparingOfflineSetlistId,
    isLiveModeOfflineReady,
    handleGoLive,
    isSetlistOfflineReady,
    isSetlistStale,
    handlePrepareOffline,
    handleLiveModeNavigate,
    handleLiveModeClose,
    handleCoachmarkDismiss,
    startLiveModeForSetlist,
  };
}
